import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { fetchVideos, fetchVideoUrl, getCachedVideosForBatch, getOrientationCountsForCategory, getExpectedTotalForCategory } from '../../utils/TrainingHelpers';
import { formatGestureForDisplay, findGestureCategory } from '../../utils/GestureCategories';
import ReplacementPopup from './ReplacementPopup';

const formatOrientation = (orientation) => {
  if (!orientation) return 'N/A';
  return orientation.charAt(0).toUpperCase() + orientation.slice(1);
};

const BatchItem = forwardRef(({ batch, setError, viewAllUsers, onVideoSelect, selectedVideos, onVideoClick, onVideoDelete, onAddReplacements, isAdrian, isLocked, selectedUser, currentUserId, page = 'training' }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [videoCount, setVideoCount] = useState(batch.videoCount);
  const [showReplacement, setShowReplacement] = useState(false);
  const [missingOrientations, setMissingOrientations] = useState([]);
  const category = findGestureCategory(batch.gesture);
  const expectedTotal = page === 'prosody'
    ? 22
    : category ? getExpectedTotalForCategory(category.id, isAdrian) : 0;
  const getVideoLabel = (orientation, index) =>
    page === 'prosody' ? `Recording #${videos.length - index}` : formatOrientation(orientation);
  const [showAddHint, setShowAddHint] = useState(false);
  const addHintTimerRef = useRef(null);
  const addVisibleRef = useRef(false);

  useEffect(() => {
    setVideoCount(batch.videoCount);
  }, [batch.videoCount]);

  useEffect(() => {
    if (videos.length > 0) {
      const areAllSelected = videos.every(video => selectedVideos.has(video.id));
      setAllSelected(areAllSelected);
    }
  }, [selectedVideos, videos]);

  useEffect(() => {
    const isAddVisible =
      isExpanded &&
      !batch.isLegacy &&
      videoCount < expectedTotal &&
      (!viewAllUsers || batch.username === currentUserId);
    if (isAddVisible && !addVisibleRef.current) {
      setShowAddHint(true);
      clearTimeout(addHintTimerRef.current);
      addHintTimerRef.current = setTimeout(() => setShowAddHint(false), 3000);
    }
    addVisibleRef.current = isAddVisible;
  }, [
    isExpanded,
    batch.isLegacy,
    videoCount,
    expectedTotal,
    viewAllUsers,
    currentUserId,
    batch.username,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(addHintTimerRef.current);
    };
  }, []);

  const handleSelectAll = (e) => {
    e.stopPropagation();
    const newAllSelected = !allSelected;
    
    const videoUpdates = videos.map(video => {
      if (newAllSelected && !selectedVideos.has(video.id)) {
        return video.id;
      } else if (!newAllSelected && selectedVideos.has(video.id)) {
        return video.id;
      }
      return null;
    }).filter(id => id !== null);

    videoUpdates.forEach(videoId => {
      onVideoSelect(videoId);
    });
  };

  const updateVideosState = (serverVideos, cachedVideos) => {
    const combinedVideos = [...cachedVideos];

    serverVideos.forEach(serverVideo => {
      if (!combinedVideos.some(video => video.id === serverVideo.id)) {
        combinedVideos.push(serverVideo);
      }
    });

    setVideos(combinedVideos);
    setVideoCount(combinedVideos.length);
    return combinedVideos;
  };

  const refreshVideos = async () => {
    if (isExpanded) {
      setIsLoading(true);
      try {
        console.log('Refreshing videos for batch:', batch.batchId);
        
        const cachedVideos = getCachedVideosForBatch(batch.batchId);
        console.log('Found cached videos for batch:', cachedVideos);
        
        const serverVideos = [];
        await fetchVideos(
          (videos) => serverVideos.push(...videos),
          setError,
          viewAllUsers,
          0,
          100,
          false,
          [],
          batch.batchId,
          selectedUser,
          page
        );
        
        updateVideosState(serverVideos, cachedVideos);
      } catch (error) {
        console.error(`Failed to refresh videos for batch ${batch.batchId}`, error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isExpanded) {
      refreshVideos();
    }
  }, [selectedUser, viewAllUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureVideosLoaded = async () => {
    if (videos.length === 0) {
      setIsLoading(true);
      try {
        const cached = getCachedVideosForBatch(batch.batchId);
        const serverVideos = [];
        await fetchVideos(
          (vids) => serverVideos.push(...vids),
          setError,
          viewAllUsers,
          0,
          100,
          false,
          [],
          batch.batchId,
          selectedUser,
          page
        );
        return updateVideosState(serverVideos, cached);
      } catch (err) {
        console.error('Failed to load videos for batch', err);
        return [];
      } finally {
        setIsLoading(false);
      }
    }
    return videos;
  };

  useImperativeHandle(ref, () => ({
    refreshVideos,
    decrementCount: () => {
      setVideoCount(prev => Math.max(0, prev - 1));
    },
    decrementCountByAmount: (amount) => {
      setVideoCount(prev => Math.max(0, prev - amount));
    },
    removeVideo: (videoId) => {
      setVideos(prev => {
        const newVideos = prev.filter(v => v.id !== videoId);
        setVideoCount(newVideos.length);
        return newVideos;
      });
    },
    removeVideos: (videoIds) => {
      setVideos(prev => {
        const newVideos = prev.filter(v => !videoIds.includes(v.id));
        setVideoCount(newVideos.length);
        return newVideos;
      });
    },
    hasVideo: (videoId) => {
      return videos.some(v => v.id === videoId);
    }
  }));

  const toggleExpand = async () => {
    if (!isExpanded && videos.length === 0) {
      setIsExpanded(true);
      setIsLoading(true);
      try {
        console.log('Initial load of videos for batch:', batch.batchId);
        
        const cachedVideos = getCachedVideosForBatch(batch.batchId);
        console.log('Found cached videos on expand:', cachedVideos);
        
        const serverVideos = [];
        await fetchVideos(
          (videos) => serverVideos.push(...videos),
          setError,
          viewAllUsers,
          0,
          100,
          false,
          [],
          batch.batchId,
          selectedUser,
          page
        );
        
        updateVideosState(serverVideos, cachedVideos);
      } catch (error) {
        console.error(`Failed to fetch videos for batch ${batch.batchId}`, error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleVideoClick = async (video, e) => {
    if (video.isCached) {
      try {
        const videoUrl = await fetchVideoUrl(video.id);
        onVideoClick({ ...video, videoUrl }, e);
      } catch (error) {
        console.error('Failed to fetch video URL:', error);
        setError('Failed to load video. Please try again.');
      }
    } else {
      onVideoClick(video, e);
    }
  };

  const handleVideoDelete = async (videoId) => {
    await onVideoDelete(videoId);
  };

  const confirmReplacement = () => {
    setShowReplacement(false);
    if (onAddReplacements) {
      const category = findGestureCategory(batch.gesture);
      onAddReplacements(batch.batchId, batch.gesture, category ? category.id : '', missingOrientations);
    }
  };

  const cancelReplacement = () => {
    setShowReplacement(false);
  };

  const handleAddClick = async (e) => {
    e.stopPropagation();
    if (batch.isLegacy) return;
    const loaded = await ensureVideosLoaded();
    if (page === 'prosody') {
      const missingCount = 22 - (loaded || videos).length;
      if (missingCount > 0 && onAddReplacements) {
        onAddReplacements(batch.batchId, batch.gesture, missingCount);
      }
      return;
    }
    const category = findGestureCategory(batch.gesture);
    if (!category) return;
    const expected = getOrientationCountsForCategory(category.id, isAdrian);
    const recorded = {};
    (loaded || videos).forEach(v => {
      const o = v.gestureOrientation || 'front';
      recorded[o] = (recorded[o] || 0) + 1;
    });
    const missing = [];
    Object.keys(expected).forEach(o => {
      const need = expected[o] - (recorded[o] || 0);
      if (need > 0) missing.push({ orientation: o, count: need });
    });
    if (missing.length > 0) {
      setMissingOrientations(missing);
      setShowReplacement(true);
    }
  };

  return (
    <>
      <div className={`batch-item${isLocked && isExpanded ? ' batch-locked' : ''}`}>
        <div
          className={`batch-header ${isExpanded ? 'expanded' : ''}`}
          onClick={toggleExpand}
        >
          <div className="batch-info">
            <div className="batch-main-info">
              <span className="batch-gesture">
                {formatGestureForDisplay(batch.gesture)}
                {batch.isLegacy && (
                  <span className="legacy-tag" title="Videos recorded before batch recording was implemented">
                    Legacy
                  </span>
                )}
              </span>
              <span className="batch-count">{videoCount} videos</span>
              {!batch.isLegacy && videoCount < expectedTotal && !isExpanded && (
                <img
                  src="/icons/caution.svg"
                  alt="Incomplete"
                  title="Batch incomplete"
                  className="batch-incomplete-icon"
                />
              )}
              {!batch.isLegacy && videoCount >= expectedTotal && !isExpanded && (
                <img
                  src="/icons/checkmark.svg"
                  alt="Complete"
                  title="Batch complete"
                  className="batch-complete-icon"
                />
              )}
              {isExpanded && videos.length > 0 && (
                <button
                  className="select-all-button"
                  onClick={handleSelectAll}
                  style={{
                    marginLeft: '10px',
                    padding: '4px 8px',
                    fontSize: '0.9em',
                    cursor: 'pointer'
                  }}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              )}
              {isExpanded &&
                !batch.isLegacy &&
                videoCount < expectedTotal &&
                (!viewAllUsers || batch.username === currentUserId) && (
                <div className="add-button-container">
                  <button className="add-replacements-button" onClick={handleAddClick}>
                    Add +
                  </button>
                  {showAddHint && <div className="help-hint" />}
                </div>
              )}
            </div>
            <div className="batch-timestamps">
              {(() => {
                const first = new Date(batch.firstTimestamp);
                const last = new Date(batch.lastTimestamp);
                const date = first.toLocaleDateString();
                const firstTime = first.toLocaleTimeString([], { hour12: true });
                const lastTime = last.toLocaleTimeString([], { hour12: true });
                return (
                  <>
                    <span className="batch-timestamp">{`${date}, ${firstTime}`}</span>
                    {batch.firstTimestamp !== batch.lastTimestamp && (
                      <>
                        <span className="timestamp-separator">→</span>
                        <span className="batch-timestamp">{lastTime}</span>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="batch-expand-icon">
            {isExpanded ? '▼' : '▶'}
          </div>
        </div>
        {isExpanded && (
          <div className="batch-videos">
            {isLoading ? (
              <div className="loading-container">
                <span>Loading videos...</span>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
              </div>
            ) : videos.length > 0 ? (
              <ul className="videos-list">
                {videos.map((video, index) => (
                  <li key={video.id} onClick={(e) => handleVideoClick(video, e)}>
                    <input
                      type="checkbox"
                      checked={selectedVideos.has(video.id)}
                      onChange={() => onVideoSelect(video.id)}
                      className="video-checkbox"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="video-thumbnail">
                      <img
                        src={video.thumbnailUrl || "/video-thumbnail.png"}
                        alt={`${getVideoLabel(video.gestureOrientation, index)} thumbnail`}
                        className="thumbnail-image"
                        onError={(e) => {
                          // Fallback to default thumbnail if loading fails
                          e.target.src = "/video-thumbnail.png";
                        }}
                      />
                      <div className="gesture-overlay">{getVideoLabel(video.gestureOrientation, index)}</div>
                    </div>
                    <div className="video-info">
                      <p>{getVideoLabel(video.gestureOrientation, index)}</p>
                      <p>{new Date(video.timestamp).toLocaleTimeString([], { hour12: true })}</p>
                      {viewAllUsers && video.username && (
                        <p className="video-username">User: {video.username}</p>
                      )}
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoDelete(video.id);
                      }}
                      className="delete-button"
                      title="Delete video"
                    >
                      <img src="/icons/red-trash-can-icon.svg" alt="Delete" className="icon" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="no-videos-message">No videos found in this batch.</div>
            )}
          </div>
        )}
        {isLocked && isExpanded && (
          <div className="batch-lock-overlay">
            <div className="batch-lock-message">
              <img src="/icons/lock.svg" alt="Locked" className="icon" />
              Replacement batch pending...
            </div>
          </div>
        )}
      </div>
      <ReplacementPopup
        visible={showReplacement}
        orientations={missingOrientations}
        onCancel={cancelReplacement}
        onConfirm={confirmReplacement}
      />
    </>
  );
});

export default BatchItem; 
